import { rivermeadowApi } from './api';

export const auth = rivermeadowApi.injectEndpoints({
    endpoints: (builder) => ({
        login: builder.mutation({
            query: ({ email, password, mfaCode = null }) => {
                const payload = { email, password };
                if (mfaCode) payload.mfa_token = mfaCode;
                return {
                    url: '/login',
                    method: 'POST',
                    body: payload,
                    credentials: 'include',
                };
            },
        }),
        activeDirectoryLogin: builder.mutation({
            query: ({ username, password }) => ({
                url: '/logins/active-directory',
                method: 'POST',
                body: {
                    username,
                    password,
                },
                data: JSON.stringify({
                    username,
                    password,
                }),
                credentials: 'include',
            }),
        }),
        idpProviderLogin: builder.query({
            query: ({ provider }) => ({
                url: `/logins/url?provider=${provider}`,
                method: 'GET',
                headers: {
                    accept: 'text/plain',
                },
                credentials: 'include',
                responseHandler: (response) => response.text(),
            }),
        }),
        getUser: builder.query({
            query: ({ id }) => ({
                url: `/users/${id}`,
                method: 'GET',
                credentials: 'include',
            }),
            providesTags: () => ['USER'],
        }),
        registerUser: builder.mutation({
            query: (payload) => ({
                url: '/registrations',
                method: 'POST',
                body: payload,
                credentials: 'include',
            }),
        }),
        getActuatorInfo: builder.query({
            query: () => ({
                url: '/actuator/info',
                method: 'GET',
                credentials: 'include',
            }),
        }),
        setDefaultUserOrg: builder.mutation({
            query: ({ userId, orgId }) => ({
                url: `/users/${userId}/organization/${orgId}/switch`,
                method: 'PUT',
            }),
        }),
        resetPasswordCode: builder.mutation({
            query: ({ email }) => ({
                url: '/passwordreset',
                method: 'POST',
                body: { email },
                credentials: 'include',
            }),
        }),
        resetPassword: builder.mutation({
            query: ({ code, password, confirmPassword }) => ({
                url: '/passwordreset',
                method: 'PUT',
                body: {
                    code,
                    password,
                    confirm_password: confirmPassword,
                },
                headers: {
                    'X-RESET-PASSWORD': sessionStorage.getItem('passwordResetToken'),
                },
                credentials: 'include',
            }),
        }),
        resetOneTimePassword: builder.mutation({
            query: ({ userId, password, confirmPassword }) => ({
                url: `/users/${userId}/onetimepasswordreset`,
                method: 'PUT',
                body: {
                    new_password: password,
                    confirm_password: confirmPassword,
                    id: userId,
                },
                credentials: 'include',
            }),
        }),
        saveMFASettings: builder.mutation({
            query: ({ email, password, mfaMethod, mfaPhoneNumber = null }) => {
                const payload = { email, password, mfa_method: mfaMethod };
                if (mfaMethod === 'sms') payload.cellphone_number = mfaPhoneNumber;
                return {
                    url: '/users/mfa',
                    method: 'PUT',
                    body: payload,
                    credentials: 'include',
                };
            },
        }),
        updateMFASettings: builder.mutation({
            query: ({ userId, mfa_details }) => ({
                url: `/users/${userId}/mfa`,
                method: 'PUT',
                body: mfa_details,
                credentials: 'include',
            }),
        }),
        sendMFAPhoneNumberCode: builder.mutation({
            query: ({ phoneNumber, email, password }) => ({
                url: `/users/mfa/cellphone-send-token`,
                method: 'POST',
                body: {
                    cellphone_number: phoneNumber,
                    email,
                    password,
                },
                credentials: 'include',
            }),
        }),
        manageAccountSendMFAPhoneNumberCode: builder.mutation({
            query: ({ phoneNumber, userId }) => ({
                url: `/users/${userId}/mfa/cellphone-send-token`,
                method: 'POST',
                body: {
                    cellphone_number: phoneNumber,
                },
                credentials: 'include',
            }),
        }),
        manageAccountVerifyMFAPhoneNumberCode: builder.mutation({
            query: ({ mfaCode, phoneNumber, userId }) => ({
                url: `/users/${userId}/mfa/cellphone-verify-token`,
                method: 'POST',
                body: {
                    mfa_token: mfaCode,
                    cellphone_number: phoneNumber,
                },
                credentials: 'include',
                responseHandler: (response) => response.text(),
            }),
        }),
        validateMFAPhoneNumberCode: builder.mutation({
            query: ({ mfaCode, phoneNumber, email, password }) => ({
                url: `/users/mfa/cellphone-verify-token`,
                method: 'POST',
                body: {
                    mfa_token: mfaCode,
                    cellphone_number: phoneNumber,
                    email,
                    password,
                },
                credentials: 'include',
            }),
        }),
        logout: builder.mutation({
            query: () => ({
                url: '/logout',
                method: 'POST',
            }),
        }),
        updateAccount: builder.mutation({
            query: ({ id, first_name, last_name }) => ({
                url: `/users/${id}/details`,
                method: 'PUT',
                body: {
                    first_name,
                    last_name,
                },
                credentials: 'include',
            }),
            invalidatesTags: ['USER'],
        }),
        changePassword: builder.mutation({
            query: ({ id, current_password, new_password, confirm_password }) => ({
                url: `/users/${id}/password`,
                method: 'PUT',
                body: {
                    current_password,
                    new_password,
                    confirm_password,
                },
                credentials: 'include',
            }),
        }),
        keepSessionAlive: builder.query({
            query: ({ userOrg }) => ({
                url: `/organizations/${userOrg}/entitlements`,
                method: 'GET',
                credentials: 'include',
            }),
        }),
    }),
});

export const {
    useLoginMutation,
    useActiveDirectoryLoginMutation,
    useResetPasswordCodeMutation,
    useResetPasswordMutation,
    useResetOneTimePasswordMutation,
    useSetDefaultUserOrgMutation,
    useSaveMFASettingsMutation,
    useUpdateMFASettingsMutation,
    useSendMFAPhoneNumberCodeMutation,
    useManageAccountSendMFAPhoneNumberCodeMutation,
    useManageAccountVerifyMFAPhoneNumberCodeMutation,
    useValidateMFAPhoneNumberCodeMutation,
    useRegisterUserMutation,
    useLazyIdpProviderLoginQuery,
    useLazyGetUserQuery,
    useGetUserQuery,
    useLazyGetActuatorInfoQuery,
    useGetActuatorInfoQuery,
    useLogoutMutation,
    useUpdateAccountMutation,
    useChangePasswordMutation,
    useLazyKeepSessionAliveQuery,
} = auth;
